import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Trash2, Upload } from 'lucide-react';
import {
  useBooking,
  useCreatives,
  useCreateCreative,
  useDeleteCreative,
  useSubmitBooking,
  useCancelBooking,
} from '@/api/ads/hooks';
import { extractErrorMessage } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { npr, fmtDate, titleCase } from '@/lib/format';

const EDITABLE = new Set(['draft', 'pending_payment', 'rejected']);

export function BookingDetailPage() {
  const { id } = useParams();
  const bookingId = id ? Number(id) : undefined;
  const booking = useBooking(bookingId);
  const creatives = useCreatives(bookingId);
  const createCreative = useCreateCreative();
  const deleteCreative = useDeleteCreative(bookingId ?? 0);
  const submit = useSubmitBooking();
  const cancel = useCancelBooking();

  const [name, setName] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [cta, setCta] = useState('');
  const [landing, setLanding] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [value, setValue] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  if (booking.isLoading) return <Spinner label="Loading booking…" />;
  if (!booking.data) return <p className="text-muted-foreground">Booking not found.</p>;

  const b = booking.data;
  const editable = EDITABLE.has(b.status);

  const uploadCreative = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bookingId) return;
    const form = new FormData();
    form.append('booking', String(bookingId));
    if (name) form.append('name', name);
    if (image) form.append('image', image);
    if (cta) form.append('cta_label', cta);
    if (landing) form.append('landing_url', landing);
    if (identifier) form.append('identifier', identifier);
    if (value) form.append('value', value);
    if (title) form.append('title', title);
    if (body) form.append('body', body);
    try {
      await createCreative.mutateAsync(form);
      toast.success('Creative added.');
      setName('');
      setImage(null);
      setCta('');
      setLanding('');
      setIdentifier('');
      setValue('');
      setTitle('');
      setBody('');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const onSubmit = async () => {
    if (!bookingId) return;
    try {
      await submit.mutateAsync(bookingId);
      toast.success('Submitted for review. Funds held from your wallet.');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const onCancel = async () => {
    if (!bookingId) return;
    try {
      await cancel.mutateAsync(bookingId);
      toast.success('Booking cancelled.');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <>
      <PageHeader
        title={`${b.product_name} booking`}
        description={`${fmtDate(b.start_date)} → ${fmtDate(b.end_date)} · ${b.daypart_label ?? 'All day'}`}
        action={<Badge status={b.status}>{b.status_display}</Badge>}
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <Card className="h-fit">
          <CardHeader title="Summary" />
          <CardBody className="space-y-2 text-sm">
            <Row label="Slots" value={String(b.slots_requested)} />
            <Row label="Unit price" value={npr(b.unit_price_locked)} />
            <Row label="Total" value={<span className="font-semibold">{npr(b.total_amount)}</span>} />
            {b.reject_reason && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-400">
                Rejected: {b.reject_reason}
              </p>
            )}
            <div className="flex gap-2 pt-2">
              {editable && (
                <Button onClick={onSubmit} loading={submit.isPending} disabled={!(creatives.data?.length)}>
                  Submit for review
                </Button>
              )}
              {['draft', 'pending_payment', 'pending_review', 'approved', 'live'].includes(b.status) && (
                <Button variant="outline" onClick={onCancel} loading={cancel.isPending}>
                  Cancel
                </Button>
              )}
            </div>
            {editable && !creatives.data?.length && (
              <p className="text-xs text-muted-foreground">Add at least one creative before submitting.</p>
            )}
          </CardBody>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Creatives" />
            <CardBody className="space-y-3">
              {creatives.isLoading ? (
                <Spinner label="Loading creatives…" />
              ) : creatives.data?.length ? (
                creatives.data.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 rounded-md border p-3">
                    {c.image ? (
                      <img src={c.image} alt={c.name} className="h-12 w-20 rounded object-cover" />
                    ) : (
                      <div className="flex h-12 w-20 items-center justify-center rounded bg-muted text-xs text-muted-foreground">
                        {c.title ? 'Push' : 'No image'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{c.name || c.title || `Creative #${c.id}`}</p>
                      <p className="truncate text-xs text-muted-foreground">{c.landing_url || c.body || '—'}</p>
                    </div>
                    <Badge status={c.status}>{c.status_display}</Badge>
                    {editable && (
                      <Button variant="ghost" size="icon" onClick={() => deleteCreative.mutate(c.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No creatives yet.</p>
              )}
            </CardBody>
          </Card>

          {editable && (
            <Card>
              <CardHeader title="Add creative" />
              <CardBody>
                <form onSubmit={uploadCreative} className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>Name</Label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Creative name" />
                    </div>
                    <div>
                      <Label>Image</Label>
                      <Input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] ?? null)} />
                    </div>
                    <div>
                      <Label>CTA label</Label>
                      <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="Shop now" />
                    </div>
                    <div>
                      <Label>Landing URL</Label>
                      <Input value={landing} onChange={(e) => setLanding(e.target.value)} placeholder="https://…" />
                    </div>
                    <div>
                      <Label>Deep-link type</Label>
                      <Select value={identifier} onChange={(e) => setIdentifier(e.target.value)}>
                        <option value="">None</option>
                        <option value="tag">Tag</option>
                        <option value="subcategory">Subcategory</option>
                        <option value="brand">Brand</option>
                      </Select>
                    </div>
                    <div>
                      <Label>Deep-link target id</Label>
                      <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 42" />
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>Push/promo title</Label>
                      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional" />
                    </div>
                    <div>
                      <Label>Push/promo body</Label>
                      <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Optional message" />
                    </div>
                  </div>
                  <Button type="submit" loading={createCreative.isPending}>
                    <Upload className="h-4 w-4" /> Add creative
                  </Button>
                </form>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{typeof label === 'string' ? titleCase(label) : label}</span>
      <span>{value}</span>
    </div>
  );
}
